/**
 * AvatarUploader — pick-a-file → crop-to-square → upload to backend.
 *
 * Small self-contained modal used from Settings and PublicProfile. When
 * upload succeeds, calls onUploaded(url) so the parent can update its
 * cached user object (and by extension AuthContext) without a full refetch.
 *
 * Design decisions:
 *  - Square 1:1 crop only (avatars are always circular downstream).
 *  - Canvas resize to 512×512 on the client so we always send ~40 KB JPEGs
 *    regardless of what the user picked. Cheap upload, cheap CDN, cheap
 *    render at every tile size.
 *  - Google users get a "Use my Google picture" reset button when they
 *    have a stored google_picture on the profile.
 */
import React, { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { Upload, X, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Render the cropped area to a 512×512 JPEG blob using canvas. `croppedPixels`
// is the {x,y,width,height} object react-easy-crop hands back on complete.
async function cropToBlob(imageSrc, croppedPixels) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  const size = 512;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#f6e9cd"; // parchment background, in case of transparency
  ctx.fillRect(0, 0, size, size);
  ctx.drawImage(
    image,
    croppedPixels.x, croppedPixels.y, croppedPixels.width, croppedPixels.height,
    0, 0, size, size,
  );
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
}

export default function AvatarUploader({ open, onClose, onUploaded, canReset }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedPixels(pixels);
  }, []);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Pick a JPEG, PNG, or WEBP image.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(f);
  };

  const onSave = async () => {
    if (!imageSrc || !croppedPixels || busy) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(imageSrc, croppedPixels);
      const fd = new FormData();
      fd.append("file", blob, "avatar.jpg");
      const { data } = await api.patch("/users/me/picture", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Profile picture updated.");
      onUploaded?.(data.picture);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/users/me/picture");
      toast.success("Reset to your Google picture.");
      onUploaded?.(data.picture);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="avatar-uploader-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="relative w-full max-w-md bg-card border border-border rounded-lg shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">Profile picture</div>
            <div className="serif text-xl text-foreground">
              {imageSrc ? "Crop your picture" : "Pick a picture"}
            </div>
          </div>
          <button
            data-testid="avatar-uploader-close"
            onClick={onClose}
            aria-label="Close"
            className="text-foreground hover:opacity-70 transition-opacity"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!imageSrc ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <button
                type="button"
                data-testid="avatar-uploader-pick"
                onClick={() => fileRef.current?.click()}
                className="btn-gold inline-flex items-center gap-2"
              >
                <Upload size={16} />
                Choose an image
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={onPick}
                className="hidden"
                data-testid="avatar-uploader-file-input"
              />
              <p className="text-[13px] text-muted-foreground text-center max-w-xs">
                JPEG, PNG, or WEBP · under 3 MB. We&apos;ll crop it to a square and
                resize to 512×512.
              </p>
              {canReset && (
                <button
                  type="button"
                  data-testid="avatar-uploader-reset"
                  onClick={onReset}
                  disabled={busy}
                  className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors inline-flex items-center gap-2 pt-2"
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
                  Reset to my Google picture
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="relative w-full aspect-square bg-[hsl(30_15%_18%)] rounded overflow-hidden">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <div className="flex items-center gap-3">
                <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Zoom</span>
                <input
                  type="range"
                  min={1} max={3} step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1"
                  data-testid="avatar-uploader-zoom"
                  aria-label="Zoom"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  data-testid="avatar-uploader-repick"
                  onClick={() => setImageSrc(null)}
                  className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
                >
                  Pick a different image
                </button>
                <button
                  type="button"
                  data-testid="avatar-uploader-save"
                  onClick={onSave}
                  disabled={busy || !croppedPixels}
                  className="btn-gold inline-flex items-center gap-2 disabled:opacity-40"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                  Save picture
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
